import React, { useEffect, useState } from 'react';
import { Simulado, User } from '../types';
import apiClient from '../auth/service';

interface Props {
    user: User;
    onSelect: (sim: Simulado) => void;
    onLogout: () => void;
    displayLanguage?: 'pt' | 'en' | 'es';
}

const SimuladoSelectionScreen: React.FC<Props> = ({ user, onSelect, onLogout, displayLanguage = 'pt' }) => {
    const [simulados, setSimulados] = useState<Simulado[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSimulados = async () => {
            try {
                const data = await apiClient.get('/simulados/my');
                setSimulados(data);
            } catch (err: any) {
                const msg = displayLanguage === 'en' 
                    ? 'Failed to load your exams.' 
                    : displayLanguage === 'es' 
                        ? 'Error ao carregar tus simulacros.' 
                        : 'Falha ao carregar seus simulados.';
                setError(msg);
            } finally {
                setLoading(false);
            }
        };
        fetchSimulados();
    }, [displayLanguage]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-900">
                <div className="text-xl font-semibold text-gray-700 dark:text-slate-200 animate-pulse">
                    {displayLanguage === 'en' ? 'Loading your exams...' : displayLanguage === 'es' ? 'Cargando tus simulacros...' : 'Carregando seus simulados...'}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-900 p-4">
            <div className="max-w-4xl w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 animate-fade-in">
                <div className="flex justify-between items-center mb-8 border-b dark:border-slate-700 pb-4">
                    <div>
                        <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                            {displayLanguage === 'en' ? `Hello, ${user.firstName}!` : displayLanguage === 'es' ? `¡Hola, ${user.firstName}!` : `Olá, ${user.firstName}!`}
                        </h2>
                        <p className="text-gray-600 dark:text-slate-400 mt-1">
                            {displayLanguage === 'en' ? 'Choose the exam you want to access today:' : displayLanguage === 'es' ? 'Elige el simulacro que deseas acceder hoy:' : 'Escolha o simulado que deseja acessar hoje:'}
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6 border border-red-100 dark:border-red-900/30">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {simulados.map((sim) => (
                        <button
                            key={sim.code}
                            onClick={() => onSelect(sim)}
                            className="group relative flex flex-col items-start p-6 bg-gray-50 dark:bg-slate-700/50 rounded-xl border-2 border-transparent hover:border-indigo-500 hover:bg-white dark:hover:bg-slate-700 transition-all duration-300 text-left shadow-sm hover:shadow-xl"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                {sim.logoUrl ? (
                                    <img src={sim.logoUrl} alt={sim.name} className="w-12 h-12 object-contain rounded-lg" />
                                ) : (
                                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                                        <i className="fas fa-book text-indigo-500 text-xl"></i>
                                    </div>
                                )}
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                        {sim.name}
                                    </h3>
                                    <span className="text-xs font-mono text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                        {sim.code}
                                    </span>
                                </div>
                            </div>
                            
                            {sim.subtitle && (
                                <p className="text-sm text-gray-600 dark:text-slate-400 line-clamp-2 mb-4">
                                    {sim.subtitle}
                                </p>
                            )}

                            <div className="mt-auto pt-4 border-t dark:border-slate-600 w-full flex items-center justify-between text-xs font-medium text-gray-500 dark:text-slate-400">
                                <div className="flex gap-4">
                                    <span><i className="fas fa-clock mr-1"></i> {Math.floor(sim.examDurationSeconds / 60)} min</span>
                                    <span><i className="fas fa-check-circle mr-1"></i> {sim.passingScore} pts</span>
                                </div>
                                <i className="fas fa-arrow-right text-indigo-500 transform group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </button>
                    ))}

                    {simulados.length === 0 && !loading && (
                        <div className="col-span-full py-12 text-center bg-gray-50 dark:bg-slate-700/30 rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-600">
                            <i className="fas fa-folder-open text-4xl text-gray-300 dark:text-slate-600 mb-4 block"></i>
                            <p className="text-gray-500 dark:text-slate-400">
                                {displayLanguage === 'en' ? (
                                    <>You don't have any enrolled exams at the moment.<br/>Contact support.</>
                                ) : displayLanguage === 'es' ? (
                                    <>No tienes simulacros inscritos en este momento.<br/>Ponte en contacto con el soporte.</>
                                ) : (
                                    <>Você não possui simulados inscritos no momento.<br/>Entre em contato com o suporte.</>
                                )}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SimuladoSelectionScreen;
